import { useState } from "react";
import { toast } from "react-hot-toast";
import { ChevronDown } from "lucide-react";
import moment from "moment";

import { createAxiosInstance } from "api/axiosConfig";
import cancelIcon from "assets/images/cancel.svg";
import downloadIcon from "assets/images/download-green.svg";
import editIcon from "assets/images/edit-green-pencil.svg";
import { Pill, PILL_COLORS } from "components/ui/Pill";
import { useDeleteImmunisation } from "features/immunizations/hooks/useDeleteImmunisation";
import { formatDateTime, formatHl7DatesInText } from "utils/dateFormatter";
import {
  formatImmunizationStatus,
  formatReceiverState,
  getImmunizationStatusColors,
  getReceiverStateColors,
  hasHealthlinkSubmission,
} from "utils/immunizationStatus";

import {
  ADMINISTRATION_METHODS,
  CONSENT_OPTIONS,
  DOSE_NUMBER_OPTIONS,
  SITE_OPTIONS,
} from "../AddVaccineForm/constants";
import { MessageHistory } from "./MessageHistory";
import styles from "./VaccineDetails.module.css";

/**
 * How one receiver answered for this dose.
 *
 * The message id and acknowledgement time are here rather than in the rejection notice
 * above because they are what a support conversation needs, and the notice is what the
 * clinician needs. The reason itself is not repeated — it is already stated in full at the
 * top of the panel, and the pill beside the receiver name is what says which of the two
 * refused it.
 */
const ReceiverStatus = ({ name, caption, receiver }) => {
  const state = receiver?.state;
  const colors = getReceiverStateColors(state);

  return (
    <div className={styles.receiverCard}>
      <div className={styles.receiverHeader}>
        <span className={styles.receiverName}>{name}</span>
        <Pill color={colors.color} bg={colors.bg} border={colors.border}>
          {formatReceiverState(state)}
        </Pill>
      </div>
      <span className={styles.receiverCaption}>{caption}</span>

      {!!receiver?.messageId && (
        <div className={styles.healthlinkStatusField}>
          <span className={styles.healthlinkStatusLabel}>Message ID:</span>
          <span className={styles.healthlinkStatusValue}>
            {receiver.messageId}
          </span>
        </div>
      )}
      {!!receiver?.ackDate && (
        <div className={styles.healthlinkStatusField}>
          <span className={styles.healthlinkStatusLabel}>Acknowledged:</span>
          <span className={styles.healthlinkStatusValue}>
            {formatDateTime(receiver.ackDate)}
          </span>
        </div>
      )}
    </div>
  );
};

/**
 * "Messages Sent", closed by default.
 *
 * Deliberately unstyled beyond a heading and a chevron. Everything around it is already
 * inside a card, and the shared Accordion would add three more boxes to that.
 */
const MessagesDisclosure = ({ patientId, immunisationId }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className={styles.messages}>
      <button
        type="button"
        className={styles.messagesToggle}
        aria-expanded={isOpen}
        onClick={() => setIsOpen((open) => !open)}
        data-testid="messages-sent-toggle"
      >
        <span className={styles.fieldLabel}>Messages Sent</span>
        <ChevronDown
          size={18}
          aria-hidden
          className={`${styles.messagesChevron} ${
            isOpen ? styles.messagesChevronOpen : ""
          }`}
        />
      </button>

      {!!isOpen && (
        <MessageHistory patientId={patientId} immunisationId={immunisationId} />
      )}
    </div>
  );
};

export const VaccineDetails = ({
  vaccine,
  onOpenEditClick,
  tenantVal,
  patientId,
  patient,
}) => {
  const { mutate: deleteImmunisation } = useDeleteImmunisation();

  const handleDownload = async () => {
    try {
      const axiosInstance = createAxiosInstance();
      const response = await axiosInstance.post(
        `${tenantVal}/patientimmunisation/print`,
        {
          patientId: patientId,
          immunisationIds: [vaccine.id],
        },
        {
          responseType: "blob",
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `immunisation-report-${patientId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Download failed:", error);
      toast.error("Failed to download immunisation report");
    }
  };

  const formatDate = (date) => {
    if (!date) return "-";

    if (typeof date === "string") {
      const momentDate = moment(date);
      if (momentDate.isValid()) {
        return momentDate.format("DD-MM-YYYY");
      }
    }

    if (Array.isArray(date) && date.length === 3) {
      return moment(new Date(date.at(0), date.at(1) - 1, date.at(2))).format(
        "DD-MM-YYYY"
      );
    }

    return "-";
  };

  const getVaccinatorName = () => {
    if (vaccine.administrationStaff) {
      const { firstName, lastName, title } = vaccine.administrationStaff;
      return (
        `${title ? title + " " : ""}${firstName || ""} ${
          lastName || ""
        }`.trim() || "-"
      );
    }
    return "-";
  };

  const getSiteLabel = (code) => {
    if (!code) return "-";
    const found = SITE_OPTIONS.find((o) => o.value === code);
    return found ? found.label : code;
  };

  const getMethodLabel = (code) => {
    if (!code) return "-";
    const found = ADMINISTRATION_METHODS.find((o) => o.value === code);
    return found ? found.label : code;
  };

  const getRiskFactorDescription = (code) => {
    const riskFactors = {
      // ── Flu + Pneumo ──────────────────────────────────────────────────────
      "X0069-A":  "Chronic Respiratory Disease",
      "X0069-B":  "Pregnant",
      "X0069-C":  "Chronic Heart Disease",
      "X0069-D":  "Chronic Renal Failure",
      "X0069-E":  "Chronic Liver Disease",
      "X0069-F":  "Chronic Neurological Disease",
      "X0069-G":  "Immunosuppressed",
      "X0069-H":  "Household contacts/carer",
      "X0069-I":  "Diabetes Mellitus",
      "X0069-J":  "BMI > 40",
      "X0069-K":  "Haemoglobinopathies",
      "X0069-M":  "Nursing home / long stay resident",
      "X0069-O":  "Carers",
      "X0069-P":  "Close contact pigs/poultry",
      "X0069-Q":  "Children on long-term aspirin",
      "X0069-R":  "HCW - Medical/Dental",
      "X0069-S":  "HCW - Nursing",
      "X0069-T":  "HCW - Health + Social Care",
      "X0069-U":  "HCW - Management/Admin",
      "X0069-V":  "HCW - General Support",
      "X0069-W":  "Other HCW",
      "X0069-X":  "Age 65 and over",
      "X0069-AL": "Down Syndrome",
      // ── Pneumo-only ───────────────────────────────────────────────────────
      "X0069-Y":  "Children <5 invasive pulmonary",
      "X0069-Z":  "CSF leak",
      "X0069-AA": "Pre/post Cochlear implant",
      "X0069-02": "Occupational exposure to metal fumes",
      "X0069-03": "Smokers and Alcoholics",
      // ── Flu-only numeric codes ─────────────────────────────────────────────
      "X0069-01": "Aged 2 to 17 years",
      "X0069-09": "Adult compromised respiratory function",
      "X0069-13": "Aged 60 to 64",
      "X0069-15": "Children with neurodevelopmental disorders",
      // ── Other active codes ─────────────────────────────────────────────────
      "X0069-05": "Inherited metabolic diseases",
      "X0069-08": "Severe mental illness",
      "X0069-16": "Aged 18-59 years",
      // ── Inactive / legacy — kept for display of older records ─────────────
      "X0069-AN": "Aged 2 to 12 years",
      "X0069-06": "Aged 50 to 64",
      "X0069-11": "Aged 18 to 64 years",
      "X0069-12": "Aged 12-17 (1st Booster Only)",
      "X0069-14": "Aged 6 months to 4 years (IC)",
      "X0069-04": "BMI > 35",
      "X0069-07": "Intellectual disability (excl. Down Syndrome)",
      "X0069-10": "Aged 5-11 years (IC)",
      "X0069-L":  "Child compromised respiratory function",
    };

    return riskFactors[code] || code;
  };

  const getAmbulatoryStatusDescription = (code) => {
    const statuses = {
      B6: "Pregnant",
      A0: "No functional limitations",
      A1: "Ambulates with assistive device",
      A2: "Wheelchair/stretcher bound",
      A3: "Comatose; non-responsive",
      A4: "Disoriented",
      A5: "Vision impaired",
      A7: "Hearing impaired",
      A8: "Speech impaired",
      A9: "Non-English speaking",
    };
    return statuses[code] || code || "-";
  };

  const getReactionPillColors = (reaction) => {
    if (!reaction || reaction === "-") return PILL_COLORS.reactionUnknown;
    const upper = reaction.toUpperCase();
    if (upper === "YES") return PILL_COLORS.reactionYes;
    if (upper === "NO") return PILL_COLORS.reactionNo;
    return PILL_COLORS.reactionUnknown;
  };

  const formatReactionText = (reaction) => {
    if (!reaction || reaction === "-") return "-";
    const upper = reaction.toUpperCase();
    if (upper === "YES") return "Yes";
    if (upper === "NO") return "No";
    if (upper === "UNKNOWN") return "Unknown";
    return reaction.charAt(0).toUpperCase() + reaction.slice(1).toLowerCase();
  };

  const getConsentLabel = (code) => {
    if (!code) return "-";
    const found = CONSENT_OPTIONS.find((o) => o.value === code);
    return found ? found.label : code;
  };

  const formatYesNo = (value) => {
    if (value === true) return "Yes";
    if (value === false) return "No";
    return "-";
  };

  const renderReactionPill = (reaction) => {
    const colors = getReactionPillColors(reaction);
    return (
      <div>
        <Pill color={colors.color} bg={colors.bg} border={colors.border}>
          {formatReactionText(reaction)}
        </Pill>
      </div>
    );
  };


  const isInfluenzaDose2 =
    vaccine?.type === "Influenza" && String(vaccine?.doseNumberCode) === "2";

  return (
    <div className={styles.card}>
      {vaccine.statusErrors?.map((msg, i) => (
        <div key={i} className={styles.statusBox}>
          <p className={styles.statusBoxTitleError}>Rejection</p>
          <p className={styles.statusBoxItemError}>
            {formatHl7DatesInText(msg)}
          </p>
        </div>
      ))}

      {vaccine.statusWarnings?.map((msg, i) => (
        <div
          key={i}
          className={`${styles.statusBox} ${styles.statusBoxWarning}`}
        >
          <p className={styles.statusBoxTitleWarning}>Warning</p>
          <p className={styles.statusBoxItemWarning}>
            {formatHl7DatesInText(msg)}
          </p>
        </div>
      ))}

      <div className={styles.contentArea}>
        <div className={styles.buttonsSection}>
          <div className={styles.buttonsGroup}>
            <button onClick={onOpenEditClick} className={styles.actionButton}>
              Edit <img src={editIcon} alt="edit" />
            </button>
            <button onClick={handleDownload} className={styles.actionButton}>
              Download <img src={downloadIcon} alt="download" />
            </button>
            <button
              className={`${styles.actionButton} ${styles.deleteButton}`}
              onClick={() => deleteImmunisation(vaccine.id)}
            >
              Cancel vaccine <img src={cancelIcon} alt="cancel" />
            </button>
          </div>
        </div>

        <div className={styles.contentBox}>
          {patient && (
            <>
              <h4 className={styles.sectionHeader}>Patient</h4>
              <div className={styles.fieldGroupRow}>
                <div className={styles.field}>
                  <h5 className={styles.fieldLabel}>Name</h5>
                  <p className={styles.fieldValue}>
                    {`${patient.firstName || ""} ${
                      patient.surname || ""
                    }`.trim() || "-"}
                  </p>
                </div>
                <div className={styles.field}>
                  <h5 className={styles.fieldLabel}>Date of Birth</h5>
                  <p className={styles.fieldValue}>
                    {patient.dateOfBirth
                      ? formatDate(patient.dateOfBirth)
                      : "-"}
                  </p>
                </div>
                <div className={styles.field}>
                  <h5 className={styles.fieldLabel}>Age</h5>
                  <p className={styles.fieldValue}>
                    {patient.dateOfBirth
                      ? moment().diff(moment(patient.dateOfBirth), "years")
                      : "-"}
                  </p>
                </div>
                {patient.gender && (
                  <div className={styles.field}>
                    <h5 className={styles.fieldLabel}>Gender</h5>
                    <p className={styles.fieldValue}>{patient.gender}</p>
                  </div>
                )}
                {patient.ppsn && (
                  <div className={styles.field}>
                    <h5 className={styles.fieldLabel}>PPSN</h5>
                    <p className={styles.fieldValue}>{patient.ppsn}</p>
                  </div>
                )}
              </div>
              <div className={styles.divider}></div>
            </>
          )}

          <div className={styles.nameTypeRow}>
            <div className={styles.field}>
              <h5 className={styles.fieldLabel}>Name</h5>
              <p className={styles.fieldValue}>{vaccine.vaccineName || "-"}</p>
            </div>
            <div className={styles.field}>
              <h5 className={styles.fieldLabel}>Type</h5>
              <p className={styles.fieldValue}>{vaccine.type || "-"}</p>
            </div>
            <div className={styles.field}>
              <h5 className={styles.fieldLabel}>Status</h5>
              <div>
                <Pill
                  color={getImmunizationStatusColors(vaccine.status).color}
                  bg={getImmunizationStatusColors(vaccine.status).bg}
                  border={getImmunizationStatusColors(vaccine.status).border}
                >
                  {formatImmunizationStatus(vaccine.status)}
                </Pill>
              </div>
            </div>
          </div>

          {vaccine.riskFactorCodes && vaccine.riskFactorCodes.length > 0 && (
            <>
              <div className={styles.divider}></div>
              <h4 className={styles.sectionHeader}>Risk factors</h4>
              <div className={styles.fieldGroup}>
                <div className={styles.field}>
                  <h5 className={styles.fieldLabel}>Medical Risk Factors</h5>
                  <div className={styles.riskFactorsList}>
                    {vaccine.riskFactorCodes.map((code, index) => (
                      <div key={index} className={styles.riskFactorItem}>
                        <div className={styles.riskFactorBullet}></div>
                        <p className={styles.riskFactorText}>
                          {getRiskFactorDescription(code)}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </>
          )}

          <div className={styles.divider}></div>

          <h4 className={styles.sectionHeader}>Administration</h4>

          <div className={styles.fieldGroupRow}>
            <div className={styles.field}>
              <h5 className={styles.fieldLabel}>Vaccinator</h5>
              <p className={styles.fieldValue}>{getVaccinatorName()}</p>
            </div>
            <div className={styles.field}>
              <h5 className={styles.fieldLabel}>Date given</h5>
              <p className={styles.fieldValue}>
                {formatDate(vaccine.dateGiven) || "-"}
              </p>
            </div>
            <div className={styles.field}>
              <h5 className={styles.fieldLabel}>Site Given</h5>
              <p className={styles.fieldValue}>
                {getSiteLabel(vaccine.siteGiven)}
              </p>
            </div>
            <div className={styles.field}>
              <h5 className={styles.fieldLabel}>Administration method</h5>
              <p className={styles.fieldValue}>
                {getMethodLabel(vaccine.administrationMethod)}
              </p>
            </div>
            {vaccine.ambulatoryStatus && (
              <div className={styles.field}>
                <h5 className={styles.fieldLabel}>Ambulatory Status</h5>
                <p className={styles.fieldValue}>
                  {getAmbulatoryStatusDescription(vaccine.ambulatoryStatus)}
                </p>
              </div>
            )}
          </div>

          <div className={styles.divider}></div>

          <h4 className={styles.sectionHeader}>Vaccine details</h4>

          <div className={styles.vaccineDetailsGrid}>
<div className={styles.field}>
              <h5 className={styles.fieldLabel}>Batch No</h5>
              <p className={styles.fieldValue}>{vaccine.batchNo || "-"}</p>
            </div>
            <div className={styles.field}>
              <h5 className={styles.fieldLabel}>Expiry date</h5>
              <p className={styles.fieldValue}>
                {formatDate(vaccine.expiryDate) || "-"}
              </p>
            </div>
            <div className={styles.field}>
              <h5 className={styles.fieldLabel}>Dose Number</h5>
              <p className={styles.fieldValue}>
                {DOSE_NUMBER_OPTIONS.find((option) => option.value === vaccine.doseNumberCode)?.label || "-"}
              </p>
            </div>
            <div className={styles.field}>
              <h5 className={styles.fieldLabel}>Dose</h5>
              <p className={styles.fieldValue}>
                {vaccine.doseMl ? `${vaccine.doseMl} ml` : "-"}
              </p>
            </div>
            {isInfluenzaDose2 && (
              <div className={styles.field}>
                <h5 className={styles.fieldLabel}>Clinically necessary</h5>
                <p className={styles.fieldValue}>
                  {formatYesNo(vaccine.clinicallyNecessary)}
                </p>
              </div>
            )}
            <div className={styles.field}>
              <h5 className={styles.fieldLabel}>Consent</h5>
              <p className={styles.fieldValue}>
                {getConsentLabel(vaccine.consentGivenCode)}
              </p>
            </div>
            <div className={styles.field}>
              <h5 className={styles.fieldLabel}>Reaction</h5>
              <div className={styles.reactionContainer}>
                {renderReactionPill(vaccine.reaction)}
                {vaccine.reactionDate && (
                  <span className={styles.reactionDate}>
                    {formatDate(vaccine.reactionDate)}
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className={styles.fieldGroup}>
            <div className={styles.field}>
              <h5 className={styles.fieldLabel}>Notes</h5>
              <p className={styles.fieldValue}>{vaccine.notes || "-"}</p>
            </div>
          </div>

          {vaccine.vaccineCodeName && (
            <>
              <div className={styles.divider}></div>
              <div className={styles.healthlinkVaccineGrid}>
                <div
                  className={`${styles.field} ${styles.healthlinkVaccineField}`}
                >
                  <h5 className={styles.fieldLabel}>Healthlink Vaccine</h5>
                  <p className={styles.fieldValue}>{vaccine.vaccineCodeName}</p>
                </div>
                <div className={styles.field}>
                  <h5 className={styles.fieldLabel}>CVX Code</h5>
                  <p className={styles.fieldValue}>
                    {vaccine.vaccineCodeCvxCode || "-"}
                  </p>
                </div>
                <div className={styles.field}>
                  <h5 className={styles.fieldLabel}>MVX Code</h5>
                  <p className={styles.fieldValue}>
                    {vaccine.vaccineCodeMvxCode || "-"}
                  </p>
                </div>
              </div>
            </>
          )}

          {/* One block per receiver, because a dose can be accepted by NIIS and refused by
              PCRS at the same time and both facts matter: it is on the national register,
              and it will not be paid for.

              This replaces a panel built on healthlinkSubmissionStatus / healthlinkAckStatus /
              healthlinkResponseCode — mirror columns written at submit time and never touched
              by asynchronous ingestion. On the three failures from the 2026-09-08 certification
              run they read "Submitted / ACK (AA)" directly beneath the red rejection above
              (KIN-1826). Those fields are still served for compatibility; nothing here needs
              them. */}
          {!!hasHealthlinkSubmission(vaccine) && (
            <>
              <div className={styles.divider}></div>
              <div className={styles.fieldGroup}>
                <div className={styles.field}>
                  {/* Which HSE specification this vaccination was reported under — the code
                      set its risk factors were chosen from. It qualifies the whole
                      submission rather than either receiver, so it sits on the heading
                      instead of below the cards, where it read as a property of the PCRS
                      one (KIN-1739). Absent until a record carries one. */}
                  <div className={styles.submissionHeader}>
                    <h5 className={styles.fieldLabel}>Healthlink Submission</h5>
                    {!!vaccine.specVersion && (
                      <span className={styles.specVersion}>
                        Spec {vaccine.specVersion}
                      </span>
                    )}
                  </div>
                  <div className={styles.receiverGrid}>
                    <ReceiverStatus
                      name="NIIS"
                      caption="National register"
                      receiver={vaccine.niis}
                    />
                    <ReceiverStatus
                      name="PCRS"
                      caption="Reimbursement claim"
                      receiver={vaccine.pcrs}
                    />
                  </div>

                  {/* The per-message layer, collapsed. The two blocks above say where the
                      dose stands; this says what was sent and what each transmission got
                      back — the only place a refused correction on an accepted dose is
                      visible (KIN-1826). It is the audit trail rather than the answer, and
                      expanded it repeats the ids and timestamps already shown above.

                      Collapsing also stops the request: MessageHistory owns its own query
                      and is not rendered until opened, so viewing a vaccine's details no
                      longer walks the patient's whole message history unasked (KIN-1856).

                      A plain disclosure rather than the shared Accordion: that component
                      draws a grey band, a bordered item and a grey header of its own,
                      which is right on a page and three nested boxes deep inside a panel
                      that is already a card (KIN-1861). */}
                  <MessagesDisclosure
                    patientId={patientId || vaccine.patientId}
                    immunisationId={vaccine.id}
                  />
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
